import React from "react";
import { connect } from "react-redux";
import language from "../../../language";
import { Table } from "../../Table";
import {
  deleteInventory,
  fetchInventories,
} from "../../../utils/queries/production/inventoryQuery";
import bloodDonationQuery from "../../../utils/queries/bloodDonationQuery";
import { DeleteModal, Modal } from "../../Modal";
import { defineRole } from "../../../utils/handleAccessRoles";
import { getStorage } from "../../../utils/storage";
import icons from "../../../constants/icons";
import ViewInventory from "../ViewInventory";
import exportPDF from "../../../utils/exportPDF";
import CardCount from "../../CardCount/CardCount";
import {
  BloodGroupFilter,
// eslint-disable-next-line no-unused-vars
  DateFilter,
  ProductFilter,
} from "../../Filters";
import { CSVLink } from "react-csv";
import { ROLE_INVENTORY } from "../../../constants/ROLES";
import moment from "moment";
import handleFilters from "../../../utils/handleFilters";

// One definition per window, used for both the count and the list the card
// opens - the two drifted apart on the stock cards precisely because each was
// written out twice. Ordered soonest first: the unit expiring tomorrow is the
// one a blood bank has to act on today.
const EXPIRY_WINDOWS = [
  { key: "one_day", days: 1, color: "red" },
  { key: "seven_days", days: 7, color: "red" },
  { key: "fourteen_days", days: 14, color: "red" },
  { key: "twenty_one_days", days: 21, color: "orange" },
  { key: "thirty_days", days: 30, color: "green" },
];

// The window AHEAD of now. Only the upper bound is sent; the lower one is
// onlyNotExpired, which the server pins to its own current timestamp, so a
// unit that expired earlier today can never be offered as still usable.
// expiredDate is a "YYYY-MM-DD HH:mm" string and is compared as one.
const expiryWindow = (days) => ({
  endExpiredDate: moment().add(days, "days").endOf("D").format("YYYY-MM-DD HH:mm"),
  onlyNotExpired: true,
});

class CloserToExpiryDate extends React.Component {
  csvDownload = React.createRef();

  state = {
    isLoading: false,
    data: [],
    page: 1,
    limit: 10,
    search_text: "",
    selectedData: {},
    delete_value: "",
    error: {},
    csvData: [],
    user: {},
    suspendReason: "",
    counts: EXPIRY_WINDOWS.reduce(
      (acc, { key }) => ({ ...acc, [key]: 0 }),
      {},
    ),
    isDisabled: true,
    // Opens on the widest window, as it did before.
    filters: expiryWindow(30),
  };

  componentDidMount = async () => {
    const user = await getStorage();
    await this.setState({ user });

    this.fetchData(true, this.state.filters);
  };

// eslint-disable-next-line no-unused-vars
  fetchData = async (isLoading, search = {}) => {
// eslint-disable-next-line no-unused-vars
    const user = await getStorage();
    const updatedFilters = {
      ...this.state.filters,
      ...search,
    };

    const formattedFilters = await this.returnFilters(updatedFilters);

    this.setState({ filters: formattedFilters });

    this.getData(isLoading, { ...formattedFilters });

    EXPIRY_WINDOWS.forEach(({ key, days }) =>
      this.getCountsData(
        isLoading,
        { ...formattedFilters, ...expiryWindow(days) },
        key,
      ),
    );
  };

  // The date picker on this tab narrows the expiry window - that is the only
  // date the tab is about. onlyNotExpired rides along so a range reaching into
  // the past still cannot bring an already-expired unit back into the list.
  toExpiryWindow = (search = {}) => {
    const { startDate, endDate, ...rest } = search;

    if (!startDate && !endDate) return search;

    return {
      ...rest,
      endExpiredDate: endDate
        ? moment(endDate).endOf("D").format("YYYY-MM-DD HH:mm")
        : undefined,
      onlyNotExpired: true,
    };
  };

  onChangeText(field, e) {
    let { error } = this.state;

    delete error[field];

    this.setState({
      [field]: e && e?.target ? e?.target?.value : e,
      error,
    });
  }

  returnFilters = async (search = {}) => {
    const { page, limit } = this.state;

    const filters = await handleFilters({
      page,
      limit,
      donation: this.props.donationId,
      id: this.props.inventoryId,
      ...search,
      // What is physically held, matching the "In stock" card's definition.
      // This read status: "pending", which is the Pending-to-be-labelled
      // status - a product still waiting for a label is not stock, so the tab
      // never showed the units whose expiry anyone needs to act on.
      inventoryStatus: "INSTOCK",
      excludeIncomingReception: true,
      onlyNotExpired: true,
    });

    return filters;
  };

  getData = async (isLoading, search = {}) => {
    try {
      const { defaultLanguage } = this.props;

      this.setState({ isLoading });
      if (search?.donationNumber) {
        const donation = await bloodDonationQuery(defaultLanguage, {
          donationNumber: search.donationNumber,
        });

        if (donation?.length > 0) {
          search.donation = donation[0]?._id || undefined;
        } else {
          this.setState({ isLoading: false });
          return;
        }
      }

      const { data, count, totalRecords } = await fetchInventories(defaultLanguage, search);

      this.setState({
        data: data,
        totalPageCount: count,
        totalRecords,
        isLoading: false,
      });
    } catch (error) {
      this.setState({
        isLoading: false,
      });
    }
  };

  getCountsData = async (isLoadingCounts, search, type) => {
    try {
      const { defaultLanguage } = this.props;

      this.setState({ isLoadingCounts });

      const filters = await this.returnFilters();

      if (search?.donationNumber) {
        const donation = await bloodDonationQuery(defaultLanguage, {
          ...search,
        });

        if (donation?.length > 0) {
          filters.donation = donation[0]?._id || undefined;
        } else {
          this.setState({ isLoading: false });
          return;
        }
      }

      let params = {
        ...filters,
        ...search,
        isCount: true,
      };

      const { data } = await fetchInventories(defaultLanguage, params);

      let { counts } = this.state;

      counts[type] = data;

      this.setState({
        counts,
        isLoadingCounts: false,
      });
    } catch (error) {
      console.log(error);
      this.setState({
        isLoadingCounts: false,
      });
    }
  };

  handlePagination(page, _tab, limit) {
    this.setState(
      {
        page,
        ...(limit != null && { limit }),
      },
      () => {
        this.getData(true, { ...this.state.filters, page });
      }
    );
  }

  handleShowModal(modal, modalTitle, selectedData = {}) {
    this.setState({
      [modal]: true,
      modalTitle: modalTitle,
      selectedData,
      reason: selectedData.reason || "",
    });
  }

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  returnTableHeaders() {
    let headers = [
      {
        title: language[this.props.defaultLanguage].donation_number,
        key: "donation.donationNumber",
        type: "link",
        onPress: (item) => this.handleShowModal("showModal", item.code, item),
      },
      {
        title: language[this.props.defaultLanguage].product_type,
        key: "component.productType.name",
      },
      {
        title: language[this.props.defaultLanguage].code,
        key: "code",
      },
      {
        title: language[this.props.defaultLanguage].product,
        key: "name",
      },
      {
        title: language[this.props.defaultLanguage].blood_group,
        key: "bloodGroup",
      },
      {
        title: language[this.props.defaultLanguage].volume,
        key: "volume",
      },
      {
        title: language[this.props.defaultLanguage].concentration,
        key: "concentrationValue",
      },
      {
        title: language[this.props.defaultLanguage].phlebotomy_date,
        key: "donation.collectionDate",
        formatTime: "ll",
        isMoment: true,
      },
      {
        title: language[this.props.defaultLanguage].expired_date,
        key: "expiredDate",
        formatTime: "lll",
        isMoment: true,
      },

      {
        title: language[this.props.defaultLanguage].labelled_by,
        key: "verifiedBy.pin",
      },
      {
        title: language[this.props.defaultLanguage].status,
        key: "status",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].stock,
        key: "stock.name",
      },

      {
        title: language[this.props.defaultLanguage].source,
        key: "sourceStock.name",
      },

      {
        title: language[this.props.defaultLanguage].reason,
        key: "reason",
      },
      {
        title: language[this.props.defaultLanguage].shipment_status,
        key: "shipmentInfo.status",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].transferred_date,
        key: "shipmentInfo.date",
        formatTime: "lll",
        isMoment: true,
      },
      {
        title: language[this.props.defaultLanguage].transferred_by,
        key: "shipmentInfo.user.pin",
      },
      {
        title: language[this.props.defaultLanguage].reception,
        key: "receptionInfo.status",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].actions,
        key: "action",
      },
    ];

    return headers;
  }

  onDelete = async () => {
    try {
      this.setState({ isDeleting: true });

      await deleteInventory(
        this.props.defaultLanguage,
        this.state.selectedData._id
      );

      this.setState({ isDeleting: false });

      this.getData(true);
      this.handleCloseModal("showDeleteModal");
    } catch (error) {
      this.setState({
        isDeleting: false,
      });
    }
  };

  downloadExcel = async () => {
    try {
      this.setState({
        isLoading: true,
      });

      const { defaultLanguage } = this.props;

      const filters = await this.returnFilters(this.state.filters);

      const { data } = await fetchInventories(defaultLanguage, filters);

      this.setState({
        isLoading: false,
        csvData: data.map((el) => {
          return {
            ...el,
            center: el?.center?.name,
            centerSite: el?.centerSite?.name,
          };
        }),
      });

      this.csvDownload.current?.link.click();
    } catch (error) {
      this.setState({
        isLoading: false,
      });
    }
  };

  downloadPDF = async () => {
    this.setState({
      isLoading: true,
    });

    const { defaultLanguage } = this.props;

    const filters = await this.returnFilters(this.state.filters);

    const { data } = await fetchInventories(defaultLanguage, filters);

    this.setState({
      isLoading: false,
    });

    exportPDF("Inventories", this.returnTableHeaders(), data);
  };

  handleRemoteSearch = async (search) => {
    await new Promise((r) => this.setState({ searching: true, page: 1 }, r));
    await this.fetchData(true, {
      donationNumber: search || search !== "" ? search : undefined,
    });

    this.setState({
      searching: false,
    });
  };

  handleSearch = async (e) => {
    const search = e?.target?.value;
    this.setState({ search_text: search });
  };

  render() {
    return (
      <>
        <div className="row">
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <ProductFilter
                handleApplyFilters={(isLoading, filters) =>
                  this.fetchData(isLoading, filters)
                }
              />

              <BloodGroupFilter
                handleApplyFilters={(isLoading, filters) =>
                  this.fetchData(isLoading, filters)
                }
              />
              <DateFilter
                handleApplyFilters={(isLoading, filters) =>
                  this.fetchData(isLoading, this.toExpiryWindow(filters))
                }
                startDate={this.state.startDate}
                endDate={this.state.endDate}
              />
            </div>
          </div>
          {EXPIRY_WINDOWS.map(({ key, days, color }) => (
            <div className="col-6 col-md-4 col-xl" key={key}>
              <CardCount
                count={this.state.counts[key]}
                title={language[this.props.defaultLanguage][key]}
                color={color}
                icon="schedule"
                isLoading={this.state.isLoadingCounts}
                onPress={() =>
                  this.getData(true, {
                    ...this.state.filters,
                    page: 1,
                    ...expiryWindow(days),
                  })
                }
              />
            </div>
          ))}
        </div>

        <Table
          data={this.state.data}
          isSearch
          page={this.state.page}
          limit={this.state.limit}
          addSearchButton
          search_text={this.state.search_text}
          searching={this.state.searching}
          handleSearch={(e) => this.onChangeText("search_text", e)}
          handlePressSearch={() =>
            this.handleRemoteSearch(this.state.search_text)
          }
          totalPageCount={this.state.totalPageCount}
          totalRecords={this.state.totalRecords}
          isLoading={this.state.isLoading}
          handlePagination={this.handlePagination.bind(this)}
          headers={this.returnTableHeaders()}
          filters={[
            {
              type: "button",
              title: language[this.props.defaultLanguage].refresh,
              button_type: "button",
              icon: icons.refresh,
              onPress: (item) => this.fetchData(true),
            },
            {
              type: "export",
              title: language[this.props.defaultLanguage].export_,
              button_type: "dropdown",
              icon: "bx bxs-download",
              options: [
                {
                  name: "PDF",
                  onPress: this.downloadPDF.bind(this),
                },
                {
                  name: "CSV",
                  onPress: this.downloadExcel.bind(this),
                },
              ],
            },
          ]}
          actions={[
            {
              canAccess: defineRole({
                roles: this.state.user.accessRole,
                menu: ROLE_INVENTORY,
                operation: "read",
              }),
              name: language[this.props.defaultLanguage].view,
              icon: icons.eye_on,
              onPress: (item) =>
                this.handleShowModal("showModal", item.code, item),
            },
          ]}
        />
        <Modal
          handleClose={this.handleCloseModal.bind(this, "showModal")}
          show={this.state.showModal}
          title={this.state.modalTitle}
          showHeaderBottomBorder={false}
          size="lg"
        >
          <ViewInventory
            handleCloseModal={this.handleCloseModal.bind(this, "showModal")}
            getData={this.fetchData.bind(this)}
            {...this.state.selectedData}
            donationId={
              this.props.donationId || this.state?.selectedData?.donation?._id
            }
            disabled={this.state.isDisabled}
          />
        </Modal>

        <Modal
          handleClose={this.handleCloseModal.bind(this, "showDeleteModal")}
          show={this.state.showDeleteModal}
          title={this.state.modalTitle}
        >
          <DeleteModal
            handleCloseModal={this.handleCloseModal.bind(
              this,
              "showDeleteModal"
            )}
            error={this.state.error.delete_password}
            value={this.state.delete_value}
            onDelete={this.onDelete.bind(this)}
            onChange={(e) => this.onChangeText("delete_value", e)}
            isDeleting={this.state.isDeleting}
            name={this.state.selectedData._id}
            getData={this.getData.bind(this)}
          />
        </Modal>
        <CSVLink
          ref={this.csvDownload}
          filename={
            "Inventories Closer to the expiration date" + new Date().getTime()
          }
          data={this.state.csvData}
        ></CSVLink>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  const { filters } = state.Filters;
  return {
    defaultLanguage,
    filters,
  };
};

export default connect(mapStateToProps)(CloserToExpiryDate);
